import { useCallback, useEffect } from 'react'

import { useRouter, usePathname } from 'next/navigation'

/**
 * Hook to track navigation history using sessionStorage
 * This runs automatically and tracks the current and previous paths
 * Must install at the root of the app to work correctly
 */
export function useNavigationTracker(): void {
  const pathname = usePathname()

  useEffect(() => {
    // Check if sessionStorage is available (client-side only)
    if (typeof window === 'undefined' || !window.sessionStorage) return

    const currentPath = sessionStorage.getItem('currentPath')

    if (currentPath && currentPath !== pathname) {
      sessionStorage.setItem('previousPath', currentPath)
    }

    sessionStorage.setItem('currentPath', pathname)
  }, [pathname])
}

export function useLastNavigationPath(): string {
  return sessionStorage.getItem('previousPath') ?? '/'
}

/**
 * Hook that provides a function to navigate back to the previous page,
 * with a fallback to the root page if there's no previous page in history.
 */
export function useBackNavigation(): (options?: { push: true }) => void {
  const router = useRouter()

  const goBack = useCallback(
    (options?: { push?: boolean }) => {
      const usePush = options?.push ?? false

      // Check if sessionStorage is available (client-side only)
      if (typeof window === 'undefined' || !window.sessionStorage) {
        if (usePush) {
          router.push('/')
        } else {
          router.back()
        }
        return
      }

      // Get the previous path from sessionStorage
      const previousPath = sessionStorage.getItem('previousPath')

      if (previousPath) {
        if (usePush) {
          // Navigate to the previous page using router.push
          router.push(previousPath)
        } else {
          // Use browser's back functionality
          router.back()
        }
      } else {
        // Fallback to root page if no previous page
        if (usePush) {
          router.push('/')
        } else {
          router.back()
        }
      }
    },
    [router],
  )

  return goBack
}
